import { Box, useMediaQuery, useTheme } from "@mui/material";
import React, { useEffect, useState } from "react";
import { COLORS } from "../../assets/theme";
import CustomButton from "../../components/common/CustomButton";
import CustomTextField from "../../components/common/CustomTextField";
import RateTemplateForm from "../../components/RateTemplateForm";
import ToastNotification from "../../components/ToastNotification";
import {
  editRateTemplateApi,
  RateTemplateType,
  useRateTemplatesQuery,
} from "../../services/RateTemplateService";
import GeneralDetailsRatesheet from "../../components/GeneralDetailsRatesheet";
import CustomLabel from "../../components/common/CustomLabel";
import { hasJwtPermission } from "../../services/ServicesHelpers";

const RateTemplatePage: React.FC = () => {
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [rateTemplate, setRateTemplate] = useState<RateTemplateType>({
    id: "",
    name: "",
    country: "",
    operator: "",
    cc: "",
    mcc: "",
    mnc: "",
    mccmnc: "",
    oldPrice: "",
    newPrice: "",
    currency: "",
    change: "",
    effectiveDate: "",
    version: 0,
    rateSheetFileName: "",
    rateSheetFormat: "CSV",
    includeLogo: false,
  });
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const { data: fetchedTemplate } = useRateTemplatesQuery();
  useEffect(() => {
    const loadRateTemplateDetails = async () => {
      try {
        if (fetchedTemplate) {
          setRateTemplate({
            id: fetchedTemplate?.id || "",
            name: fetchedTemplate?.name || "",
            country: fetchedTemplate?.country,
            operator: fetchedTemplate?.operator,
            cc: fetchedTemplate?.cc || "",
            mcc: fetchedTemplate?.mcc || "",
            mnc: fetchedTemplate?.mnc || "",
            mccmnc: fetchedTemplate?.mccmnc || "",
            oldPrice: fetchedTemplate?.oldPrice || "",
            newPrice: fetchedTemplate?.newPrice || "",
            currency: fetchedTemplate?.currency || "",
            change: fetchedTemplate?.change || "",
            effectiveDate: fetchedTemplate?.effectiveDate || "",
            version: fetchedTemplate?.version || 0,
            rateSheetFileName:
              fetchedTemplate?.rateSheetFileName.toString() || "",
            rateSheetFormat: fetchedTemplate?.rateSheetFormat || "CSV",
            includeLogo: fetchedTemplate?.includeLogo || false,
          });
        }
      } catch (err: any) {
        ToastNotification.error(err.message);
      }
    };

    loadRateTemplateDetails();
  }, [fetchedTemplate]);

  const handleUpdate = async () => {
    try {
      setIsUploading(true);
      const { id, ...payload } = rateTemplate;
      const updated = await editRateTemplateApi(payload);
      if (updated && updated.version) {
        setRateTemplate((prev) => ({ ...prev, ...updated }));
      }
      ToastNotification.success("Rate template updated successfully!");
    } catch (err) {
      ToastNotification.error(
        "An error occurred while updating the rate template."
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleFieldChange = (
    field: keyof RateTemplateType,
    value: string | boolean
  ) => {
    setRateTemplate((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCustomFormChange = (
    field: keyof RateTemplateType,
    value: string | boolean
  ) => {
    setRateTemplate((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const formStyles = {
    padding: { xs: 2, sm: 5 },
    borderRadius: "12px",
    backgroundColor: "#fff",
  };

  const generalDetails = (
    <GeneralDetailsRatesheet
      rateTemplate={rateTemplate}
      handleFieldChange={handleFieldChange}
      handleCustomFormChange={handleCustomFormChange}
    />
  );

  const additionalComponents = [
    <Box
      key="row-1"
      display="flex"
      flexDirection={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      gap={2}
      sx={{ marginTop: 2 }}
    >
      <CustomTextField
        label="Account Name"
        value={rateTemplate.name}
        bgColor={COLORS.WhiteBackground}
        fullWidth
        placeholder="Account Name"
        onChange={(event) => handleFieldChange("name", event.target.value)}
      />
      <CustomTextField
        label="Country"
        value={rateTemplate.country}
        bgColor={COLORS.WhiteBackground}
        fullWidth
        placeholder="Country"
        onChange={(event) => handleFieldChange("country", event.target.value)}
      />
      <CustomTextField
        label="Operator"
        value={rateTemplate.operator}
        bgColor={COLORS.WhiteBackground}
        fullWidth
        placeholder="Operator"
        onChange={(event) => handleFieldChange("operator", event.target.value)}
      />
    </Box>,
    <Box
      key="row-2"
      display="flex"
      flexDirection={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      gap={2}
      sx={{ marginTop: 2 }}
    >
      <CustomTextField
        label="CC"
        value={rateTemplate.cc}
        bgColor={COLORS.WhiteBackground}
        fullWidth
        placeholder="CC"
        onChange={(event) => handleFieldChange("cc", event.target.value)}
      />
      <CustomTextField
        label="MCC"
        value={rateTemplate.mcc}
        bgColor={COLORS.WhiteBackground}
        fullWidth
        placeholder="MCC"
        onChange={(event) => handleFieldChange("mcc", event.target.value)}
      />
      <CustomTextField
        label="MNC"
        value={rateTemplate.mnc}
        bgColor={COLORS.WhiteBackground}
        fullWidth
        placeholder="MNC"
        onChange={(event) => handleFieldChange("mnc", event.target.value)}
      />
    </Box>,
    <Box
      key="row-3"
      display="flex"
      flexDirection={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      gap={2}
      sx={{ marginTop: 2 }}
    >
      <CustomTextField
        label="MCCMNC"
        value={rateTemplate.mccmnc}
        bgColor={COLORS.WhiteBackground}
        fullWidth
        placeholder="MCCMNC"
        onChange={(event) => handleFieldChange("mccmnc", event.target.value)}
      />
      <CustomTextField
        label="Old Price"
        value={rateTemplate.oldPrice}
        bgColor={COLORS.WhiteBackground}
        fullWidth
        placeholder="Old Price"
        onChange={(event) => handleFieldChange("oldPrice", event.target.value)}
      />
      <CustomTextField
        label="New Price"
        value={rateTemplate.newPrice}
        bgColor={COLORS.WhiteBackground}
        fullWidth
        placeholder="New Price"
        onChange={(event) => handleFieldChange("newPrice", event.target.value)}
      />
    </Box>,
    <Box
      key="row-4"
      display="flex"
      flexDirection={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      gap={2}
      sx={{ marginTop: 2 }}
    >
      <CustomTextField
        label="Currency"
        value={rateTemplate.currency}
        size={isSmallScreen ? "small" : "medium"}
        bgColor={COLORS.WhiteBackground}
        placeholder="Currency"
        onChange={(event) => handleFieldChange("currency", event.target.value)}
      />
      <CustomTextField
        label="Change"
        size={isSmallScreen ? "small" : "medium"}
        value={rateTemplate.change}
        bgColor={COLORS.WhiteBackground}
        placeholder="Change"
        onChange={(event) => handleFieldChange("change", event.target.value)}
      />
      <CustomTextField
        label="Effective Date"
        size={isSmallScreen ? "small" : "medium"}
        value={rateTemplate.effectiveDate}
        bgColor={COLORS.WhiteBackground}
        placeholder="Effective Date"
        onChange={(event) =>
          handleFieldChange("effectiveDate", event.target.value)
        }
      />
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        flex="1"
      >
        <CustomButton
          variant="contained"
          label={isUploading ? "Updating..." : "Update"}
          textColor={COLORS.textPrimary}
          bgColor={COLORS.GOLD}
          disabled={
            isUploading ||
            !hasJwtPermission("PUT_RATE_TEMPLATE")
          }
          onClick={handleUpdate}
        />
      </Box>
    </Box>,
  ];

  return (
    <>
      <Box py={{ xs: 1, sm: 2, md: 4 }} px={{ xs: 2, sm: 2, md: 4 }} width="100%">
        <CustomLabel
          text="General Details"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginBottom={1}
        />

        <RateTemplateForm
          additionalComponents={[generalDetails]}
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            backgroundColor: "#fff",
            borderRadius: "12px",
            width: "100%",
            padding: { xs: 1, sm: 2, md: 4 },
          }}
        />
      </Box>
      <Box py={{ xs: 1, sm: 2, md: 4 }} px={{ xs: 2, sm: 2, md: 4 }} width="100%">
        <CustomLabel
          text="Rate Sheet Mapping List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginBottom={1}
        />

        <RateTemplateForm
          additionalComponents={additionalComponents}
          layout="column"
          sx={{
            ...formStyles,
            padding: { xs: 1, sm: 2, md: 4 },
            flexDirection: { xs: "column", sm: "row" },
            flexWrap: "wrap",
            gap: { xs: 2, sm: 3, md: 4 },
            width: "100%",
          }}
        />
      </Box>
    </>
  );
};

export default RateTemplatePage;
